const extractPmwConstant = (key: string): string => {
    const pmwConfig = window.PMW as unknown as Record<string, string>;
    return pmwConfig[key] || "";
};

export interface UpsellFeatureMetadata {
    premiumLabelKey?: string;
    premiumPlusLabelKey?: string;
    featureNames?: string[];
}

export const PMW_FEATURE_CONSTANTS = {
    BG_REMOVALS: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_BG_REMOVALS"),
    VIDEO_BG_REMOVALS: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_VIDEO_BG_REMOVALS"),
    CUSTOM_FONT: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_CUSTOM_FONT"),
    EMAIL_SENDS: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EMAIL_SENDS"),
    SCHEDULE_POST: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_SCHEDULE_POST"),
    CONTENT_PLANNER: extractPmwConstant("PREMIUM_PLUS_ONLY_FEATURE_NAME_CONTENT_PLANNER"),
    SPELL_CHECK: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_SPELL_CHECK"),
    PUBLISH_EMAIL_CAMPAIGN: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_PUBLISH_EMAIL_CAMPAIGN"),
    BRANDS: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_BRANDS"),
    MORE_BRANDS: extractPmwConstant("PREMIUM_PLUS_ONLY_FEATURE_NAME_MORE_BRANDS"),
    EDITOR_BRANDS: extractPmwConstant("PREMIUM_PLUS_ONLY_FEATURE_NAME_EDITOR_BRANDS"),
    CUSTOM_DOMAIN_ENABLED: extractPmwConstant("PREMIUM_PLUS_ONLY_FEATURE_IS_CUSTOM_DOMAIN_ENABLED"),
    EMAIL_CAMPAIGNS_PERFORMANCE: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGNS_PERFORMANCE"),
    EMAIL_CAMPAIGNS_AUDIENCE: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGNS_AUDIENCE"),
    ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM: extractPmwConstant("PREMIUM_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS"),
    ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS: extractPmwConstant("PREMIUM_PLUS_ONLY_FEATURE_ADD_SOCIAL_MEDIA_ACCOUNTS"),
    EMAIL_TEMPLATE_DOWNLOAD: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EMAIL_TEMPLATE_DOWNLOAD"),
    AUTO_CAPTION: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_AUTO_CAPTION"),
    SOCIAL_POST_METRICS: extractPmwConstant("PREMIUM_ONLY_FEATURE_SOCIAL_POST_METRICS"),
    SOCIAL_MEDIA_MULTI_POSTING: extractPmwConstant("PREMIUM_ONLY_SOCIAL_MEDIA_MULTI_POSTING"),
    TEXT_TO_IMAGE: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_IMAGE"),
    TEXT_TO_IMAGE_PANEL: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_IMAGE_PANEL"),
    AI_VIDEO: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_AI_VIDEO"),
    TEXT_TO_SPEECH: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_SPEECH"),
    TEXT_TO_SPEECH_AI_WRITER: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_TEXT_TO_SPEECH_AI_WRITER"),
    POST_DOWNLOAD_SOCIAL_MEDIA_SIZES: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_POST_DOWNLOAD_SOCIAL_MEDIA_SIZES"),
    AI_REWRITE_EMAIL_CAMPAIGN: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT"),
    MORE_EVENTS_PREMIUM: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_MORE_EVENTS"),
    MORE_EVENTS_PREMIUM_PLUS: extractPmwConstant("PREMIUM_PLUS_ONLY_FEATURE_NAME_MORE_EVENTS"),
    EVENTS_REMOVE_WATERMARK: extractPmwConstant("PREMIUM_PLUS_ONLY_FEATURE_EVENTS_REMOVE_WATERMARK"),
    EVENTS_AI_WRITER: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EVENTS_AI_WRITER"),
    EVENTS_GUESTS_INFORMATION: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EVENTS_GUESTS_INFORMATION"),
    RECURRING_EVENTS: extractPmwConstant("PREMIUM_PLUS_ONLY_FEATURE_NAME_RECURRING_EVENTS"),
    EVENTS_LIMITED_REGISTRATONS: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EVENTS_LIMITED_REGISTRATONS"),
    EVENTS_VIEW_ANALYTICS: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EVENTS_VIEW_ANALYTICS"),
    EVENTS_REMINDERS: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EVENTS_REMINDERS"),
    EVENT_ONE_CLICK_PROMOTION: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EVENT_ONE_CLICK_PROMOTION"),
    POST_FREE_DOWNLOAD: extractPmwConstant("FEATURE_NAME_POST_FREE_DOWNLOAD"),
    EMAIL_CAMPAIGN_AI_REDESIGN: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_EMAIL_CAMPAIGN_AI_REDESIGN"),
    DESIGN_EDITOR_AI_REWRITE: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_DESIGN_EDITOR_AI_REWRITE"),
    AI_CHAT_CREATE_MORE: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_CREATE_MORE"),
    AI_CHAT_KEEP_CREATING: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_KEEP_CREATING"),
    AI_CHAT_AUDIO_INPUT: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_AI_CHAT_AUDIO_INPUT"),
    AI_CHAT_SUBTITLE: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_AI_SUBTITLES"),
    CREDITS: extractPmwConstant("PREMIUM_ONLY_FEATURE_NAME_CREDITS"),
    UNLIMITED_PAGES: extractPmwConstant("PREMIUM_ONLY_UNLIMITED_PAGES_FEATURE"),
};

/**
 * Upsell key for the Getty dialogs — the limit-reached dialogs and the remove-watermark pill. Unlike the
 * entries above it is not a backend feature flag, because Getty limits and watermarking are enforced
 * client-side; it is a local identifier, the same shape as `postdownloaddialogforevents` below.
 */
export const GETTY_STOCK_CONTENT_UPSELL = "gettystockcontent";

export const UPSELL_FEATURE_METADATA_MAP: Record<string, UpsellFeatureMetadata> = {
  [GETTY_STOCK_CONTENT_UPSELL]: {
    // Premium Plus falls back to this label; its own list says "Everything included in Premium" rather
    // than repeating the downloads row, so there is nothing to filter out there.
    premiumLabelKey: 'pmwjs_unlimited_watermark_free_downloads',
    featureNames: [PMW_FEATURE_CONSTANTS.POST_FREE_DOWNLOAD],
  },
  [PMW_FEATURE_CONSTANTS.BG_REMOVALS]: {
    premiumLabelKey: 'pmwjs_includes_ai_background_remover',
    featureNames: [PMW_FEATURE_CONSTANTS.BG_REMOVALS],
  },
  [PMW_FEATURE_CONSTANTS.VIDEO_BG_REMOVALS]: {
    premiumLabelKey: 'pmwjs_includes_ai_background_remover',
    featureNames: [PMW_FEATURE_CONSTANTS.BG_REMOVALS],
  },
  [PMW_FEATURE_CONSTANTS.CUSTOM_FONT]: {
    premiumPlusLabelKey: 'pmwjs_includes_custom_fonts',
    featureNames: [PMW_FEATURE_CONSTANTS.BRANDS, PMW_FEATURE_CONSTANTS.MORE_BRANDS],
  },
  [PMW_FEATURE_CONSTANTS.EMAIL_SENDS]: {
    premiumLabelKey: 'pmwjs_includes_email_sending',
    featureNames: [PMW_FEATURE_CONSTANTS.EMAIL_SENDS],
  },
  [PMW_FEATURE_CONSTANTS.SCHEDULE_POST]: {
    premiumLabelKey: 'pmwjs_includes_scheduling',
    featureNames: [PMW_FEATURE_CONSTANTS.SCHEDULE_POST],
  },
  [PMW_FEATURE_CONSTANTS.CONTENT_PLANNER]: {
    premiumLabelKey: 'pmwjs_includes_content_planner',
    featureNames: [PMW_FEATURE_CONSTANTS.SCHEDULE_POST],
  },
  [PMW_FEATURE_CONSTANTS.SPELL_CHECK]: {
    premiumLabelKey: 'pmwjs_includes_spellcheck',
    featureNames: [PMW_FEATURE_CONSTANTS.SPELL_CHECK],
  },
  [PMW_FEATURE_CONSTANTS.PUBLISH_EMAIL_CAMPAIGN]: {
    premiumLabelKey: 'pmwjs_includes_email_sending',
    featureNames: [PMW_FEATURE_CONSTANTS.EMAIL_SENDS],
  },
  [PMW_FEATURE_CONSTANTS.BRANDS]: {
    premiumLabelKey: 'pmwjs_includes_1_brand_kit',
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_brand_kits',
    featureNames: [PMW_FEATURE_CONSTANTS.BRANDS, PMW_FEATURE_CONSTANTS.MORE_BRANDS],
  },
  [PMW_FEATURE_CONSTANTS.MORE_BRANDS]: {
    premiumLabelKey: 'pmwjs_includes_1_brand_kit',
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_brand_kits',
    featureNames: [PMW_FEATURE_CONSTANTS.BRANDS, PMW_FEATURE_CONSTANTS.MORE_BRANDS],
  },
  [PMW_FEATURE_CONSTANTS.EDITOR_BRANDS]: {
    premiumLabelKey: 'pmwjs_includes_1_brand_kit',
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_brand_kits',
    featureNames: [PMW_FEATURE_CONSTANTS.BRANDS, PMW_FEATURE_CONSTANTS.MORE_BRANDS],
  },
  [PMW_FEATURE_CONSTANTS.CUSTOM_DOMAIN_ENABLED]: {
    premiumPlusLabelKey: 'pmwjs_includes_custom_domains',
    featureNames: [PMW_FEATURE_CONSTANTS.CUSTOM_DOMAIN_ENABLED],
  },
  [PMW_FEATURE_CONSTANTS.EMAIL_CAMPAIGNS_PERFORMANCE]: {
    premiumLabelKey: 'pmwjs_includes_email_performance',
    featureNames: [PMW_FEATURE_CONSTANTS.EMAIL_SENDS],
  },
  [PMW_FEATURE_CONSTANTS.EMAIL_CAMPAIGNS_AUDIENCE]: {
    premiumLabelKey: 'pmwjs_includes_mailing_lists',
    featureNames: [PMW_FEATURE_CONSTANTS.EMAIL_SENDS],
  },
  [PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM]: {
    premiumLabelKey: 'pmwjs_includes_5_social_accounts',
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_social_accounts',
    featureNames: [PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM, PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS]: {
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_social_accounts',
    featureNames: [PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM, PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.EMAIL_TEMPLATE_DOWNLOAD]: {
    premiumLabelKey: 'pmwjs_includes_email_sending',
    featureNames: [PMW_FEATURE_CONSTANTS.EMAIL_SENDS],
  },
  [PMW_FEATURE_CONSTANTS.AUTO_CAPTION]: {
    premiumLabelKey: 'pmwjs_includes_ai_caption_writer',
    featureNames: [PMW_FEATURE_CONSTANTS.AUTO_CAPTION],
  },
  [PMW_FEATURE_CONSTANTS.SOCIAL_POST_METRICS]: {
    premiumLabelKey: 'pmwjs_includes_social_media_performance',
    featureNames: [PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM, PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.SOCIAL_MEDIA_MULTI_POSTING]: {
    premiumLabelKey: 'pmwjs_includes_multiple_posts',
    featureNames: [PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM, PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE]: {
    premiumLabelKey: 'pmwjs_includes_ai_images',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE_PANEL]: {
    premiumLabelKey: 'pmwjs_includes_ai_images',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.AI_VIDEO]: {
    premiumLabelKey: 'pmwjs_includes_ai_video',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.TEXT_TO_SPEECH]: {
    premiumLabelKey: 'pmwjs_includes_ai_voice',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.TEXT_TO_SPEECH_AI_WRITER]: {
    premiumLabelKey: 'pmwjs_includes_ai_writer',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.POST_DOWNLOAD_SOCIAL_MEDIA_SIZES]: {
    premiumLabelKey: 'pmwjs_includes_5_social_accounts',
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_social_accounts',
    featureNames: [PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM, PMW_FEATURE_CONSTANTS.ADD_SOCIAL_MEDIA_ACCOUNTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.AI_REWRITE_EMAIL_CAMPAIGN]: {
    premiumLabelKey: 'pmwjs_includes_ai_writer',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM]: {
    premiumLabelKey: 'pmwjs_includes_5_event_pages_month',
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_event_pages_month',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS]: {
    premiumLabelKey: 'pmwjs_includes_5_event_pages_month',
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_event_pages_month',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.EVENTS_REMOVE_WATERMARK]: {
    premiumPlusLabelKey: 'pmwjs_includes_watermark_removal_for_events',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.EVENTS_AI_WRITER]: {
    premiumLabelKey: 'pmwjs_includes_ai_writer',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.EVENTS_GUESTS_INFORMATION]: {
    premiumLabelKey: 'pmwjs_includes_more_guest_info',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.RECURRING_EVENTS]: {
    premiumPlusLabelKey: 'pmwjs_includes_recurring_events',
    featureNames: [PMW_FEATURE_CONSTANTS.RECURRING_EVENTS],
  },
  [PMW_FEATURE_CONSTANTS.EVENTS_LIMITED_REGISTRATONS]: {
    premiumLabelKey: 'pmwjs_includes_unlimited_registrations',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.EVENTS_VIEW_ANALYTICS]: {
    premiumLabelKey: 'pmwjs_includes_event_metrics',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.EVENTS_REMINDERS]: {
    premiumLabelKey: 'pmwjs_includes_event_reminders',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.EVENT_ONE_CLICK_PROMOTION]: {
    premiumLabelKey: 'pmwjs_includes_one_click_event_promotion',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  postdownloaddialogforevents: {
    premiumLabelKey: 'pmwjs_includes_5_event_pages_month',
    premiumPlusLabelKey: 'pmwjs_includes_unlimited_event_pages_month',
    featureNames: [PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM, PMW_FEATURE_CONSTANTS.MORE_EVENTS_PREMIUM_PLUS],
  },
  [PMW_FEATURE_CONSTANTS.EMAIL_CAMPAIGN_AI_REDESIGN]: {
    premiumLabelKey: 'pmwjs_includes_ai_email_redesign',
    featureNames: [PMW_FEATURE_CONSTANTS.AUTO_CAPTION],
  },
  [PMW_FEATURE_CONSTANTS.DESIGN_EDITOR_AI_REWRITE]: {
    premiumLabelKey: 'pmwjs_includes_ai_rewrite',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.AI_CHAT_CREATE_MORE]: {
    premiumLabelKey: 'pmwjs_includes_create_with_ai',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.AI_CHAT_KEEP_CREATING]: {
    premiumLabelKey: 'pmwjs_includes_create_with_ai',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.AI_CHAT_AUDIO_INPUT]: {
    premiumLabelKey: 'pmwjs_includes_ai_voice',
    featureNames: [PMW_FEATURE_CONSTANTS.TEXT_TO_IMAGE],
  },
  [PMW_FEATURE_CONSTANTS.AI_CHAT_SUBTITLE]: {
    premiumLabelKey: 'pmwjs_includes_ai_subtitles',
    featureNames: [PMW_FEATURE_CONSTANTS.AI_CHAT_SUBTITLE],
  },
  [PMW_FEATURE_CONSTANTS.UNLIMITED_PAGES]: {
    premiumLabelKey: 'pmwjs_includes_unlimited_pages',
  },
};